import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BriefcaseBusiness, Loader2, Trash2 } from 'lucide-react';
import { consultingApi } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { BUSINESS_TYPE_META, STAGE_COLORS } from '../types/consulting';
import { BUSINESS_TYPES, DEFAULT_BUSINESS_TYPE, normalizeBusinessType } from '../utils/businessTaxonomy';

const STAGES = ['prospect', 'assessment', 'pilot', 'implementation', 'retainer', 'completed'];
const DOC_TYPES = ['discovery', 'problem', 'pilot', 'proposal', 'maintenance'];
const CONSULTING_TABS = [
  { id: 'client_360', label: 'Client Snapshot' },
  { id: 'documents', label: 'Documents' },
  { id: 'orchestration', label: 'Orchestration & PDF' },
  { id: 'readiness_stats', label: 'Readiness & Sessions' },
  { id: 'user_management', label: 'User Access' },
  { id: 'calendar_settings', label: 'Calendar Settings' },
];

const ADMIN_CONSULTING_CACHE_KEY_PREFIX = 'wowrolla_admin_consulting_clients_cache_v1';

const readAdminCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    return cached && typeof cached === 'object' ? cached : null;
  } catch {
    return null;
  }
};

const writeAdminCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage issues
  }
};

const parseFilenameFromDisposition = (disposition) => {
  if (!disposition || typeof disposition !== 'string') return null;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]).replace(/["']/g, '').trim();
    } catch {
      return utfMatch[1].replace(/["']/g, '').trim();
    }
  }
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || null;
};

const DOC_META = {
  discovery: {
    label: 'Discovery Call',
    desc: 'Preparation guide for consultant-led discovery calls.',
    chips: ['PRE-CALL CHECKLIST', '12 QUESTIONS', 'CLOSING SCRIPT'],
    accent: '#FFE800',
  },
  problem: {
    label: 'Problem Statement',
    desc: 'Core pain and quantified impact summary.',
    chips: ['EXECUTIVE SUMMARY', 'BUSINESS IMPACT', 'KPI TARGETS'],
    accent: '#FF6B35',
  },
  pilot: {
    label: 'Pilot Plan',
    desc: '30-day scoped implementation pilot roadmap.',
    chips: ['WEEK-BY-WEEK', 'RISK MITIGATION', 'SUCCESS CRITERIA'],
    accent: '#00D4FF',
  },
  proposal: {
    label: 'Full Proposal',
    desc: 'Complete implementation + ROI proposal.',
    chips: ['ROI SUMMARY', 'TIMELINE', 'COST OF INACTION'],
    accent: '#FFE800',
  },
  maintenance: {
    label: 'Retainer Plan',
    desc: 'Ongoing optimization and operations playbook.',
    chips: ['MONTHLY CADENCE', 'KPIs', 'GROWTH ROADMAP'],
    accent: '#39FF14',
  },
};

const EMPTY_CLIENT = {
  bizName: '',
  contactName: '',
  email: '',
  businessType: DEFAULT_BUSINESS_TYPE,
  bizSize: 'small',
  stage: 'prospect',
  budget: 'unsure',
  currentTools: '',
  notes: '',
};

const docKey = (clientId, docType) => `${clientId}_${docType}`;

const WEEKDAY_LABELS = [
  { index: 1, label: 'Mon' },
  { index: 2, label: 'Tue' },
  { index: 3, label: 'Wed' },
  { index: 4, label: 'Thu' },
  { index: 5, label: 'Fri' },
  { index: 6, label: 'Sat' },
  { index: 0, label: 'Sun' },
];
const DAY_LOOKUP = {
  mon: 1,
  monday: 1,
  tue: 2,
  tues: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thur: 4,
  thurs: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6,
  sun: 0,
  sunday: 0,
};
const SLOT_DURATION_MINUTES = 20;
const SLOT_GAP_MINUTES = 20;
const SLOT_INTERVAL_MINUTES = SLOT_DURATION_MINUTES + SLOT_GAP_MINUTES;
const GRID_START_MINUTES = 6 * 60;
const GRID_END_MINUTES = 22 * 60;

const buildGridMinutes = () => {
  const values = [];
  for (let minute = GRID_START_MINUTES; minute <= GRID_END_MINUTES - SLOT_DURATION_MINUTES; minute += SLOT_INTERVAL_MINUTES) {
    values.push(minute);
  }
  return values;
};

const GRID_MINUTES = buildGridMinutes();

const formatMinutes = (minutes) => {
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
};

const parseMinutes = (raw = '') => {
  const normalized = String(raw || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const match = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = match[3];
  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes < 0 || minutes > 59 || hours < 1 || hours > 12) {
    return null;
  }

  if (suffix === 'AM') {
    if (hours === 12) hours = 0;
  } else if (hours !== 12) {
    hours += 12;
  }

  return (hours * 60) + minutes;
};

const parseDayIndex = (raw = '') => {
  const key = String(raw || '').trim().toLowerCase().replace(/\./g, '');
  return DAY_LOOKUP[key];
};

const isDayInRange = (day, startDay, endDay) => {
  if (startDay <= endDay) return day >= startDay && day <= endDay;
  return day >= startDay || day <= endDay;
};

const slotId = (dayIndex, minuteValue) => `${dayIndex}|${minuteValue}`;

const expandSlotsToSelectionSet = (slots = []) => {
  const selected = new Set();

  (Array.isArray(slots) ? slots : []).forEach((rawLine) => {
    const line = String(rawLine || '').trim();
    if (!line || /^timezone\s*:/i.test(line)) return;

    const rangeMatch = line.match(/^([A-Za-z.]+)\s*-\s*([A-Za-z.]+)\s*,\s*([^-]+)\s*-\s*(.+)$/i);
    if (rangeMatch) {
      const startDay = parseDayIndex(rangeMatch[1]);
      const endDay = parseDayIndex(rangeMatch[2]);
      const startMinute = parseMinutes(rangeMatch[3]);
      const endMinute = parseMinutes(rangeMatch[4]);

      if (startDay != null && endDay != null && startMinute != null && endMinute != null && endMinute > startMinute) {
        WEEKDAY_LABELS.forEach(({ index: dayIndex }) => {
          if (!isDayInRange(dayIndex, startDay, endDay)) return;
          for (let cursor = startMinute; cursor + SLOT_DURATION_MINUTES <= endMinute; cursor += SLOT_INTERVAL_MINUTES) {
            if (cursor >= GRID_START_MINUTES && cursor <= GRID_END_MINUTES - SLOT_DURATION_MINUTES) {
              selected.add(slotId(dayIndex, cursor));
            }
          }
        });
      }
      return;
    }

    const singleWindowMatch = line.match(/^([A-Za-z.]+)\s*,\s*([^-]+)\s*-\s*(.+)$/i);
    if (singleWindowMatch) {
      const dayIndex = parseDayIndex(singleWindowMatch[1]);
      const startMinute = parseMinutes(singleWindowMatch[2]);
      const endMinute = parseMinutes(singleWindowMatch[3]);

      if (dayIndex != null && startMinute != null && endMinute != null && endMinute > startMinute) {
        for (let cursor = startMinute; cursor + SLOT_DURATION_MINUTES <= endMinute; cursor += SLOT_INTERVAL_MINUTES) {
          if (cursor >= GRID_START_MINUTES && cursor <= GRID_END_MINUTES - SLOT_DURATION_MINUTES) {
            selected.add(slotId(dayIndex, cursor));
          }
        }
      }
      return;
    }

    const singleSlotMatch = line.match(/^([A-Za-z.]+)\s+(.+)$/i);
    if (singleSlotMatch) {
      const dayIndex = parseDayIndex(singleSlotMatch[1]);
      const minuteValue = parseMinutes(singleSlotMatch[2]);
      if (dayIndex != null && minuteValue != null && GRID_MINUTES.includes(minuteValue)) {
        selected.add(slotId(dayIndex, minuteValue));
      }
    }
  });

  return selected;
};

const serializeSelectionSetToSlots = (selectionSet) => {
  const ordered = [];
  WEEKDAY_LABELS.forEach(({ index: dayIndex, label }) => {
    GRID_MINUTES.forEach((minuteValue) => {
      if (selectionSet.has(slotId(dayIndex, minuteValue))) {
        ordered.push(`${label} ${formatMinutes(minuteValue)}`);
      }
    });
  });
  return ordered;
};

const buildPresetBusinessHours = () => {
  const set = new Set();
  [1, 2, 3, 4, 5].forEach((dayIndex) => {
    GRID_MINUTES.forEach((minuteValue) => {
      const inMorning = minuteValue >= 9 * 60 && minuteValue <= (12 * 60) - SLOT_DURATION_MINUTES;
      const inAfternoon = minuteValue >= 13 * 60 && minuteValue <= (17 * 60) - SLOT_DURATION_MINUTES;
      if (inMorning || inAfternoon) {
        set.add(slotId(dayIndex, minuteValue));
      }
    });
  });
  return set;
};

const normalizeClient = (client) => ({
  ...client,
  bizName: client.bizName || client.biz_name || '',
  contactName: client.contactName || client.contact_name || '',
  businessType: normalizeBusinessType(client.businessType || client.business_type || client.industry || '', DEFAULT_BUSINESS_TYPE),
  currentTools: client.currentTools || client.current_tools || '',
  bizSize: client.bizSize || client.biz_size || 'small',
  createdAt: client.createdAt || client.created_at,
  updatedAt: client.updatedAt || client.updated_at,
  phone: client.phone || client.phone_number || client.phone_number_raw || client.phone_number_normalized || client.phone || null,
  discoveryScheduledAt: client.discoveryScheduledAt || client.discovery_scheduled_at || client.discovery_scheduled_at_raw || null,
});

const AdminConsultingPage = () => {
  const { adminTheme, toggleAdminTheme } = useTheme();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [activeDocTab, setActiveDocTab] = useState('discovery');
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [draftClient, setDraftClient] = useState(EMPTY_CLIENT);
  const [stageFilter, setStageFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [activeConsultingTab, setActiveConsultingTab] = useState('documents');
  const [orchestrationBusy, setOrchestrationBusy] = useState('');
  const [syncResponse, setSyncResponse] = useState(null);
  const [customWorkflows, setCustomWorkflows] = useState([]);
  const [pitchPackage, setPitchPackage] = useState(null);
  const [userManagement, setUserManagement] = useState(null);
  const [readinessStats, setReadinessStats] = useState(null);
  const [hub360, setHub360] = useState(null);
  const [credentialForm, setCredentialForm] = useState({ newUsername: '', newPassword: '' });
  const [customWorkflowForm, setCustomWorkflowForm] = useState({
    workflowName: '',
    objective: '',
    fallbackTools: 'firecrawl,serper,manual_queue',
  });
  const [marketingForm, setMarketingForm] = useState({
    campaignLabel: 'consulting_follow_up',
    challenge: '',
    budgetMonthly: '',
    preferredSlot: '',
    note: '',
  });
  const [calendarSettingsForm, setCalendarSettingsForm] = useState({
    title: 'Book a 20-minute consultation',
    timezone: 'Local timezone confirmed by email',
    booking_url: '',
  });
  const [calendarAvailability, setCalendarAvailability] = useState(() => buildPresetBusinessHours());

  const selectedClient = clients.find((client) => client.id === selectedClientId) || null;

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map((stage) => [stage, 0]));
    clients.forEach((client) => {
      if (counts[client.stage] !== undefined) counts[client.stage] += 1;
    });
    return counts;
  }, [clients]);

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const matchesStage = stageFilter === 'all' ? true : client.stage === stageFilter;
      const q = search.trim().toLowerCase();
      const matchesSearch = !q
        ? true
        : `${client.bizName} ${client.contactName} ${client.email}`.toLowerCase().includes(q);
      return matchesStage && matchesSearch;
    });
  }, [clients, stageFilter, search]);

  const getClientContext = (client) => {
    if (!client) return '';
    const readiness = client.readiness_snapshot || {};
    const readinessScore = readiness.readiness_score;
    const paybackMonths = readiness.payback_months;
    const readinessPart = client.readiness_completed
      ? ` | readiness ${readinessScore ?? 'n/a'}/100 | payback ${paybackMonths ?? 'n/a'} mo`
      : '';
    return `${client.businessType} | ${client.stage} | ${client.budget || 'unsure'} | ${client.currentTools || 'tool stack pending'}${readinessPart}`;
  };

  const inferBusinessType = (client) => {
    return normalizeBusinessType(client?.businessType || client?.business_type || '', DEFAULT_BUSINESS_TYPE);
  };

  const loadClients = async (headerKey) => {
    const keyToUse = headerKey || adminKey;
    if (!keyToUse) return;

    const cacheKey = `${ADMIN_CONSULTING_CACHE_KEY_PREFIX}:${keyToUse}`;
    const cached = readAdminCache(cacheKey);
    if (cached?.clients) {
      setClients(Array.isArray(cached.clients) ? cached.clients : []);
      if (!selectedClientId && Array.isArray(cached.clients) && cached.clients.length) {
        setSelectedClientId(cached.clients[0].id);
      }
    }

    try {
      const response = await consultingApi.getClients(keyToUse);
      const nextClients = (response.data || []).map(normalizeClient);
      setClients(nextClients);
      writeAdminCache(cacheKey, { clients: nextClients, updatedAt: new Date().toISOString() });
      if (!selectedClientId && nextClients.length) {
        setSelectedClientId(nextClients[0].id);
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Failed to load clients.');
    }
  };

  const loadCalendarSettings = async (keyToUse) => {
    if (!keyToUse) return;
    try {
      const response = await consultingApi.getCalendarSettings(keyToUse);
      const data = response?.data?.settings || response?.data || {};
      const slots = Array.isArray(data.slots) ? data.slots : [];
      setCalendarSettingsForm({
        title: data.title || 'Book a 20-minute consultation',
        timezone: data.timezone || 'Local timezone confirmed by email',
        booking_url: data.booking_url || '',
      });
      setCalendarAvailability(slots.length ? expandSlotsToSelectionSet(slots) : new Set());
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Failed to load calendar settings.');
    }
  };

  const saveCalendarSettings = async () => {
    if (!adminKey) return;
    try {
      const slots = serializeSelectionSetToSlots(calendarAvailability);

      await consultingApi.updateCalendarSettings(adminKey, {
        title: calendarSettingsForm.title,
        timezone: calendarSettingsForm.timezone,
        booking_url: calendarSettingsForm.booking_url,
        slots,
      });
      await loadCalendarSettings(adminKey);
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || 'Failed to save calendar settings.');
    }
  };

  const toggleAvailabilityCell = (dayIndex, minuteValue) => {
    const id = slotId(dayIndex, minuteValue);
    setCalendarAvailability((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearAvailability = () => {
    setCalendarAvailability(new Set());
  };

  const applyBusinessHoursPreset = () => {
    setCalendarAvailability(buildPresetBusinessHours());
  };

  useEffect(() => {
    const stored = localStorage.getItem('wowrolla_admin_key') || '';
    let key = stored;
    if (!key) {
      key = window.prompt('Enter WOWROLLA_ADMIN_KEY') || '';
      if (key) localStorage.setItem('wowrolla_admin_key', key);
    }
    setAdminKey(key);

    const hydrate = async () => {
      if (!key) return;
      try {
        const cacheKey = `${ADMIN_CONSULTING_CACHE_KEY_PREFIX}:${key}`;
        const cached = readAdminCache(cacheKey);
        if (cached?.clients) {
          const nextClients = Array.isArray(cached.clients) ? cached.clients.map(normalizeClient) : [];
          setClients(nextClients);
          if (nextClients.length) {
            setSelectedClientId(nextClients[0].id);
          }
        }
        const response = await consultingApi.getClients(key);
        const nextClients = (response.data || []).map(normalizeClient);
        setClients(nextClients);
        writeAdminCache(cacheKey, { clients: nextClients, updatedAt: new Date().toISOString() });
        if (nextClients.length) {
          setSelectedClientId(nextClients[0].id);
        }
        await loadCalendarSettings(key);
      } catch (loadError) {
        setError(loadError?.response?.data?.detail || 'Failed to load clients.');
      }
    };

    hydrate();
  }, []);

  const loadDocsFor = async (clientId) => {
    if (!adminKey || !clientId) return;
    try {
      const response = await consultingApi.getDocs(adminKey, clientId);
      const next = {};
      (response.data || []).forEach((doc) => {
        next[docKey(clientId, doc.doc_type || doc.docType)] = doc.content;
      });
      setDocs((prev) => ({ ...prev, ...next }));
    } catch {
      // No-op: doc fetching is opportunistic.
    }
  };

  const selectClient = (clientId) => {
    setSelectedClientId(clientId);
    setActiveDocTab('discovery');
    loadDocsFor(clientId);
  };

  const handleGenerate = async (clientId, docType) => {
    if (!adminKey) return;
    setLoading(docType);
    try {
      const response = await consultingApi.generateDoc(adminKey, clientId, docType);
      const content = response.data?.content || '';
      setDocs((prev) => ({ ...prev, [docKey(clientId, docType)]: content }));
      await loadDocsFor(clientId);
    } catch (generateError) {
      setError(generateError?.response?.data?.detail || 'Failed to generate document.');
    } finally {
      setLoading('');
    }
  };

  const copyOutput = async () => {
    const content = docs[docKey(selectedClientId, activeDocTab)] || '';
    if (!content) return;
    await navigator.clipboard.writeText(content);
  };

  const handleStageChange = async (clientId, stage) => {
    const previous = clients;
    setClients((prev) => prev.map((client) => (client.id === clientId ? { ...client, stage } : client)));
    try {
      await consultingApi.updateStage(adminKey, clientId, stage);
    } catch {
      setClients(previous);
      setError('Stage update failed.');
    }
  };

  const saveNotes = async (clientId, notes) => {
    try {
      await consultingApi.updateNotes(adminKey, clientId, notes);
    } catch {
      setError('Unable to save notes.');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await consultingApi.deleteClient(adminKey, clientId);
      const next = clients.filter((client) => client.id !== clientId);
      setClients(next);
      if (selectedClientId === clientId) setSelectedClientId(next[0]?.id || null);
    } catch {
      setError('Delete failed.');
    }
  };

  const addClient = async () => {
    if (!draftClient.bizName || !draftClient.email) {
      setError('Business name and email are required.');
      return;
    }

    try {
      const createResponse = await consultingApi.submitIntake({
        biz_name: draftClient.bizName,
        contact_name: draftClient.contactName || 'Unknown',
        email: draftClient.email,
        business_type: draftClient.businessType,
        biz_size: draftClient.bizSize,
        pains: BUSINESS_TYPE_META[draftClient.businessType]?.pains || [],
        budget: draftClient.budget,
        current_tools: draftClient.currentTools,
        goal: null,
      });
      setAddingClient(false);
      setDraftClient(EMPTY_CLIENT);
      await loadClients(adminKey);
      if (createResponse?.data?.client_id) {
        setSelectedClientId(createResponse.data.client_id);
      }
    } catch {
      setError('Failed to add client.');
    }
  };

  const loadOrchestrationArtifacts = useCallback(async (clientId) => {
    if (!adminKey || !clientId) return;

    try {
      const [workflowRes, pitchRes] = await Promise.allSettled([
        consultingApi.listCustomWorkflows(adminKey, clientId),
        consultingApi.getPitchPackage(adminKey, clientId),
      ]);

      if (workflowRes.status === 'fulfilled') {
        setCustomWorkflows(workflowRes.value?.data || []);
      } else {
        setCustomWorkflows([]);
      }

      if (pitchRes.status === 'fulfilled') {
        setPitchPackage(pitchRes.value?.data || null);
      } else {
        setPitchPackage(null);
      }
    } catch {
      setCustomWorkflows([]);
      setPitchPackage(null);
    }
  }, [adminKey]);

  const syncSelectedClientToOrchestration = async () => {
    if (!selectedClient) return;
    setOrchestrationBusy('sync');
    try {
      const response = await consultingApi.syncClientToOrchestration(adminKey, selectedClient.id, {
        business_type: inferBusinessType(selectedClient),
      });
      setSyncResponse(response.data || null);
      await loadOrchestrationArtifacts(selectedClient.id);
    } catch (syncError) {
      setError(syncError?.response?.data?.detail || 'Orchestration sync failed.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const createCustomWorkflow = async () => {
    if (!selectedClient) return;
    if (!customWorkflowForm.workflowName || !customWorkflowForm.objective) {
      setError('Workflow name and objective are required for custom workflow.');
      return;
    }

    setOrchestrationBusy('custom-workflow');
    try {
      await consultingApi.addCustomWorkflow(adminKey, selectedClient.id, {
        workflow_name: customWorkflowForm.workflowName,
        objective: customWorkflowForm.objective,
        fallback_tools: customWorkflowForm.fallbackTools.split(',').map((item) => item.trim()).filter(Boolean),
      });
      setCustomWorkflowForm((prev) => ({ ...prev, workflowName: '', objective: '' }));
      await loadOrchestrationArtifacts(selectedClient.id);
    } catch (workflowError) {
      setError(workflowError?.response?.data?.detail || 'Unable to save custom workflow.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const generatePitchPackage = async () => {
    if (!selectedClient) return;
    setOrchestrationBusy('pitch');
    try {
      const response = await consultingApi.generatePitchPackage(adminKey, selectedClient.id, {
        business_type: inferBusinessType(selectedClient),
        include_custom_workflows: true,
      });
      setPitchPackage(response.data?.package || null);
      await loadOrchestrationArtifacts(selectedClient.id);
    } catch (pitchError) {
      setError(pitchError?.response?.data?.detail || 'Failed to generate pitch package.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const downloadPitchPdf = async () => {
    if (!selectedClient) return;
    if (!pitchPackage) {
      setError('Generate a pitch package first, then download the PDF.');
      return;
    }
    setOrchestrationBusy('download-pdf');
    try {
      const response = await consultingApi.downloadPitchPdf(adminKey, selectedClient.id);
      const rawData = response?.data;
      const blob = rawData instanceof Blob ? rawData : new Blob([rawData], { type: 'application/pdf' });
      if (blob.size < 100) {
        const possibleError = await blob.text().catch(() => '');
        throw new Error(possibleError || 'Received invalid PDF payload from server');
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
      const suggestedName = parseFilenameFromDisposition(contentDisposition);
      link.download = suggestedName || `wowrolla-prepilot-${selectedClient.bizName || selectedClient.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(downloadError?.response?.data?.detail || downloadError?.message || 'PDF download failed.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const loadUserManagement = useCallback(async (clientId) => {
    if (!adminKey || !clientId) return;
    try {
      const response = await consultingApi.getUserManagement(adminKey, clientId);
      setUserManagement(response.data || null);
    } catch (managementError) {
      setUserManagement(null);
      setError(managementError?.response?.data?.detail || 'Unable to load user management profile.');
    }
  }, [adminKey]);

  const loadReadinessStats = useCallback(async (clientId) => {
    if (!adminKey || !clientId) return;
    try {
      const response = await consultingApi.getClientReadinessStats(adminKey, clientId);
      setReadinessStats(response.data || null);
    } catch {
      setReadinessStats(null);
    }
  }, [adminKey]);

  const loadHub360 = useCallback(async (clientId) => {
    if (!adminKey || !clientId) return;
    try {
      const response = await consultingApi.getClientHub360(adminKey, clientId);
      setHub360(response.data || null);
    } catch (loadError) {
      setHub360(null);
      setError(loadError?.response?.data?.detail || 'Unable to load client 360 profile.');
    }
  }, [adminKey]);

  const pushLeadToMarketingHub = async () => {
    if (!selectedClient) return;
    setOrchestrationBusy('marketing-link');
    try {
      await consultingApi.linkClientToMarketingHub(adminKey, selectedClient.id, {
        lead_type: 'consulting',
        source_page: '/admin/consulting',
        challenge: marketingForm.challenge || null,
        budget_monthly: marketingForm.budgetMonthly ? Number(marketingForm.budgetMonthly) : null,
        preferred_slot: marketingForm.preferredSlot || null,
        note: marketingForm.note || null,
        campaign_label: marketingForm.campaignLabel || 'consulting_follow_up',
      });
      await loadHub360(selectedClient.id);
    } catch (linkError) {
      setError(linkError?.response?.data?.detail || 'Failed to link this client to Marketing Hub.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const startDiscovery = async () => {
    if (!selectedClient) return;
    setOrchestrationBusy('discovery');
    try {
      await consultingApi.startDiscoveryCall(adminKey, selectedClient.id, {
        business_type: inferBusinessType(selectedClient),
      });
      await loadClients(adminKey);
      await loadUserManagement(selectedClient.id);
    } catch (discoveryError) {
      setError(discoveryError?.response?.data?.detail || 'Failed to start discovery flow.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  const submitCredentialReset = async () => {
    if (!selectedClient) return;
    if (!credentialForm.newUsername && !credentialForm.newPassword) {
      setError('Provide a new username or password.');
      return;
    }

    setOrchestrationBusy('reset');
    try {
      await consultingApi.resetUserCredentials(adminKey, selectedClient.id, {
        new_username: credentialForm.newUsername || null,
        new_password: credentialForm.newPassword || null,
      });
      setCredentialForm({ newUsername: '', newPassword: '' });
      await loadUserManagement(selectedClient.id);
    } catch (resetError) {
      setError(resetError?.response?.data?.detail || 'Failed to reset credentials.');
    } finally {
      setOrchestrationBusy('');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedClientId) {
      loadOrchestrationArtifacts(selectedClientId);
      loadUserManagement(selectedClientId);
      loadReadinessStats(selectedClientId);
      loadHub360(selectedClientId);
    }
  }, [selectedClientId, loadOrchestrationArtifacts, loadUserManagement, loadReadinessStats, loadHub360]);

  return (
    <div className="min-h-screen bg-[#080808] text-white admin-theme-shell admin-consulting-theme-shell" data-admin-theme={adminTheme} data-testid="admin-consulting-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="field-heading mb-2">Admin Workspace</p>
            <h1 className="font-unbounded text-2xl md:text-3xl font-bold text-white">Consulting Hub</h1>
            <p className="text-sm text-[#888] mt-2">Client pipeline - AI document generation - Stage tracking</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleAdminTheme}
              className="px-3 py-2 rounded-full border border-[#1c1c1c] bg-[#0f0f0f] text-xs font-semibold text-white hover:border-[#FFE800] transition-colors"
            >
              Admin {adminTheme === 'light' ? 'Light' : 'Dark'}
            </button>
            <div className="border border-[rgba(57,255,20,0.4)] bg-[rgba(57,255,20,0.08)] text-[#39FF14] font-mono text-xs px-3 py-2">
              {clients.length} clients
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-[#FF6B35] mb-4">{error}</p>}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
          <button onClick={() => setStageFilter('all')} className={`bg-[#0f0f0f] border border-[#1c1c1c] p-3 text-left ${stageFilter === 'all' ? 'border-[#FFE800]' : ''}`}>
            <p className="field-heading">All</p>
            <p className="font-unbounded text-sm text-white">{clients.length}</p>
          </button>
          {STAGES.map((stage) => (
            <button key={stage} onClick={() => setStageFilter(stage)} className={`bg-[#0f0f0f] border border-[#1c1c1c] p-3 text-left ${stageFilter === stage ? 'border-[#FFE800]' : ''}`}>
              <p className="font-mono text-[10px] tracking-widest uppercase" style={{ color: STAGE_COLORS[stage] }}>{stage}</p>
              <p className="font-unbounded text-sm text-white">{stageCounts[stage]}</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[680px] border border-[#1c1c1c]">
          <section className="bg-[#0d0d0d] border-r border-[#1c1c1c] h-full overflow-y-auto">
            <div className="p-4 space-y-3 border-b border-[#1c1c1c]">
              <Link to="/admin/dashboard" className="text-sm text-[#888] hover:text-[#FFE800] inline-flex items-center gap-1"><BriefcaseBusiness size={14} /> Back to dashboard</Link>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by business or contact" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
              <button onClick={() => setAddingClient((prev) => !prev)} className="w-full rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(255,232,0,0.08)] border-[rgba(255,232,0,0.35)] text-[#FFE800] hover:bg-[rgba(255,232,0,0.14)]">
                + Add Client
              </button>
            </div>

            {addingClient && (
              <div className="p-4 border-b border-[#1c1c1c] space-y-2">
                <input value={draftClient.bizName} onChange={(e) => setDraftClient((prev) => ({ ...prev, bizName: e.target.value }))} placeholder="Business name" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                <input value={draftClient.contactName} onChange={(e) => setDraftClient((prev) => ({ ...prev, contactName: e.target.value }))} placeholder="Contact name" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                <input value={draftClient.email} onChange={(e) => setDraftClient((prev) => ({ ...prev, email: e.target.value }))} placeholder="Email" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                <select value={draftClient.businessType} onChange={(e) => setDraftClient((prev) => ({ ...prev, businessType: e.target.value }))} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none">
                  {BUSINESS_TYPES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
                <select value={draftClient.bizSize} onChange={(e) => setDraftClient((prev) => ({ ...prev, bizSize: e.target.value }))} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none">
                  <option value="small">1-10</option>
                  <option value="medium">11-50</option>
                  <option value="large">51-200</option>
                </select>
                <select value={draftClient.budget} onChange={(e) => setDraftClient((prev) => ({ ...prev, budget: e.target.value }))} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none">
                  <option value="under5k">Under $5k</option>
                  <option value="5k-15k">$5k-$15k</option>
                  <option value="15k-30k">$15k-$30k</option>
                  <option value="30kplus">$30k+</option>
                  <option value="unsure">Not sure</option>
                </select>
                <input value={draftClient.currentTools} onChange={(e) => setDraftClient((prev) => ({ ...prev, currentTools: e.target.value }))} placeholder="Current tools" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                <textarea value={draftClient.notes} onChange={(e) => setDraftClient((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Notes" rows={3} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                <div className="flex gap-2">
                  <button onClick={addClient} className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors bg-[rgba(255,232,0,0.08)] border-[rgba(255,232,0,0.35)] text-[#FFE800] hover:bg-[rgba(255,232,0,0.14)]">Save</button>
                  <button onClick={() => setAddingClient(false)} className="rounded-none font-mono text-[10px] tracking-widest uppercase px-4 py-2 border border-[#1c1c1c] text-[#888]">Cancel</button>
                </div>
              </div>
            )}

            {filteredClients.map((client) => (
              <div
                role="button"
                tabIndex={0}
                key={client.id}
                onClick={() => selectClient(client.id)}
                onKeyDown={(e) => e.key === 'Enter' && selectClient(client.id)}
                className={`bg-[#0f0f0f] border-b border-[#111] p-4 cursor-pointer hover:bg-[#121212] ${selectedClientId === client.id ? 'border-l-2 border-[#FFE800] bg-[#111]' : ''}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-unbounded text-sm font-semibold text-white">{client.bizName}</h3>
                  <button type="button" onClick={(e) => { e.stopPropagation(); deleteClient(client.id); }} className="text-[#FF6B35] hover:opacity-80"><Trash2 size={14} /></button>
                </div>
                <p className="text-xs text-[#888]">{client.contactName} - {client.email}</p>
                <p className="text-xs text-[#888] mt-1">{BUSINESS_TYPE_META[client.businessType]?.emoji} {BUSINESS_TYPE_META[client.businessType]?.label}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="font-mono text-[10px] px-2 py-1 border border-[#1c1c1c]" style={{ color: STAGE_COLORS[client.stage] }}>{client.stage}</span>
                  <select value={client.stage} onClick={(e) => e.stopPropagation()} onChange={(e) => handleStageChange(client.id, e.target.value)} className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-xs px-2 py-1 rounded-none">
                    {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </section>

          <section className="p-4 md:p-6">
            {!selectedClient ? (
              <div className="h-full border border-dashed border-[#1c1c1c] grid place-items-center text-[#666] text-sm">
                Select a client to generate consulting documents
              </div>
            ) : (
              <div>
                <div className="flex overflow-x-auto border-b border-[#1c1c1c] mb-4 consulting-tab-strip">
                  {CONSULTING_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveConsultingTab(tab.id)}
                      className="font-mono text-[10px] tracking-widest uppercase px-4 py-3 border-b-2 whitespace-nowrap"
                      style={{ borderColor: activeConsultingTab === tab.id ? '#FFE800' : 'transparent', color: activeConsultingTab === tab.id ? '#FFE800' : '#666' }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="bg-[#0d0d0d] border border-[#1c1c1c] px-4 py-2 flex gap-6 flex-wrap font-mono text-[9px] mb-4">
                  <span>{getClientContext(selectedClient)}</span>
                </div>

                <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                    <p className="field-heading mb-2">Phone</p>
                    <p className="text-sm text-white">{selectedClient?.phone || hub360?.identity?.phone || 'Not provided'}</p>
                  </div>
                  <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                    <p className="field-heading mb-2">Discovery Scheduled</p>
                    <p className="text-sm text-white">{(selectedClient?.discoveryScheduledAt || selectedClient?.discovery_scheduled_at) ? new Date(selectedClient?.discoveryScheduledAt || selectedClient?.discovery_scheduled_at).toLocaleString() : 'Not scheduled'}</p>
                  </div>
                  <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                    <p className="field-heading mb-2">Stage</p>
                    <p className="text-sm" style={{ color: STAGE_COLORS[selectedClient?.stage] }}>{selectedClient?.stage}</p>
                  </div>
                  <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                    <p className="field-heading mb-2">Business Type</p>
                    <p className="text-sm text-white">{BUSINESS_TYPE_META[selectedClient?.businessType]?.label || selectedClient?.businessType}</p>
                  </div>
                </div>

                {activeConsultingTab === 'client_360' ? (
                  <>
                    <h2 className="font-unbounded text-sm font-semibold text-white">Client 360 Command Center</h2>
                    <p className="text-sm text-[#888] mt-1 mb-4">Refine-style single pane for identity, stack, workflows, session intelligence, marketing handoff, and Run My AI Flow performance.</p>

                    {!hub360 ? (
                      <div className="border border-dashed border-[#1c1c1c] p-5 text-[#666] text-sm">Loading client 360 profile...</div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Linked account</p>
                            <p className="text-sm text-white">{hub360.identity?.linked_user?.username || 'Not linked'}</p>
                            <p className="text-xs text-[#888] mt-1">{hub360.identity?.email || selectedClient?.email}</p>
                          </div>
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Finder sessions</p>
                            <p className="text-sm text-[#FFE800]">{hub360.identity?.finder_sessions_count || 0}</p>
                            <p className="text-xs text-[#888] mt-1">Consulting booked: {hub360.identity?.consulting_booked_count || 0}</p>
                          </div>
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Stack</p>
                            <p className="text-sm text-white">{hub360.stack?.items_count || 0} tools</p>
                            <p className="text-xs text-[#888] mt-1">${hub360.stack?.total_monthly_cost || 0}/mo est.</p>
                          </div>
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Run My AI Flow</p>
                            <p className="text-sm text-[#39FF14]">{hub360.run_my_ai_flow?.aggregate?.completion_rate || 0}% complete</p>
                            <p className="text-xs text-[#888] mt-1">{hub360.run_my_ai_flow?.aggregate?.completed_runs || 0}/{hub360.run_my_ai_flow?.aggregate?.total_runs || 0} runs</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="border border-[#1c1c1c] p-4">
                            <p className="field-heading mb-2">Workflow + Tool Stack Context</p>
                            <div className="mb-3">
                              <p className="font-mono text-[10px] uppercase tracking-wider text-[#888] mb-1">Selected workflows</p>
                              <div className="flex gap-1 flex-wrap">
                                {(hub360.workflow?.selected_workflows || []).length === 0 ? (
                                  <span className="text-xs text-[#666]">No workflows captured yet.</span>
                                ) : (hub360.workflow?.selected_workflows || []).map((wf) => (
                                  <span key={wf} className="font-mono text-[10px] px-2 py-1 border border-[#1c1c1c] text-[#00D4FF]">{wf}</span>
                                ))}
                              </div>
                            </div>

                            <p className="font-mono text-[10px] uppercase tracking-wider text-[#888] mb-1">Tools in stack</p>
                            {(hub360.stack?.items || []).length === 0 ? (
                              <div className="text-xs text-[#666]">No stack items linked to this client profile yet.</div>
                            ) : (
                              <div className="space-y-1 max-h-[240px] overflow-y-auto">
                                {(hub360.stack?.items || []).map((item) => (
                                  <div key={`${item.tool_id}-${item.tool_name}`} className="bg-[#0d0d0d] border border-[#1c1c1c] px-2 py-2 text-xs flex items-center justify-between">
                                    <span className="text-white">{item.tool_name || item.tool_id}</span>
                                    <span className="text-[#888]">${item.monthly_cost || 0}/mo</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="border border-[#1c1c1c] p-4">
                            <p className="field-heading mb-2">Marketing Hub Handoff (By Client ID)</p>
                            <div className="grid grid-cols-1 gap-2 mb-2">
                              <input value={marketingForm.campaignLabel} onChange={(e) => setMarketingForm((prev) => ({ ...prev, campaignLabel: e.target.value }))} placeholder="Campaign label" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                              <input value={marketingForm.challenge} onChange={(e) => setMarketingForm((prev) => ({ ...prev, challenge: e.target.value }))} placeholder="Primary challenge for campaign message" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                              <div className="grid grid-cols-2 gap-2">
                                <input value={marketingForm.budgetMonthly} onChange={(e) => setMarketingForm((prev) => ({ ...prev, budgetMonthly: e.target.value }))} placeholder="Budget monthly" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                                <input value={marketingForm.preferredSlot} onChange={(e) => setMarketingForm((prev) => ({ ...prev, preferredSlot: e.target.value }))} placeholder="Preferred slot" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                              </div>
                              <textarea rows={3} value={marketingForm.note} onChange={(e) => setMarketingForm((prev) => ({ ...prev, note: e.target.value }))} placeholder="Campaign note for Marketing Hub" className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none" />
                            </div>
                            <button onClick={pushLeadToMarketingHub} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-60">
                              {orchestrationBusy === 'marketing-link' ? 'Syncing...' : 'Send Lead To Marketing Hub'}
                            </button>

                            <div className="mt-3 text-xs text-[#888]">
                              Last bridge: {hub360.marketing_hub?.link?.last_synced_at ? new Date(hub360.marketing_hub.link.last_synced_at).toLocaleString() : 'Not linked yet'}
                            </div>
                          </div>
                        </div>

                        <div className="border border-[#1c1c1c] p-4">
                          <p className="field-heading mb-2">Recent Session Intelligence</p>
                          {(hub360.identity?.recent_finder_sessions || []).length === 0 ? (
                            <div className="text-xs text-[#666]">No finder sessions linked to this client account yet.</div>
                          ) : (
                            <div className="space-y-1 max-h-[260px] overflow-y-auto">
                              {(hub360.identity?.recent_finder_sessions || []).map((row) => (
                                <div key={row.sessionId} className="bg-[#0d0d0d] border border-[#1c1c1c] p-2 text-xs flex items-center justify-between gap-2">
                                  <span className="text-white">{row.sessionId}</span>
                                  <span className="text-[#888]">score {row.consultingScore || 0}</span>
                                  <span className="text-[#888]">{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="border border-[#1c1c1c] p-4">
                          <p className="field-heading mb-2">Tool Purchase Intent Activity</p>
                          <p className="text-xs text-[#888] mb-2">Events recorded when users launch affiliate/tool buy links from their stack dashboard.</p>
                          {(hub360.commerce?.recent_purchase_intents || []).length === 0 ? (
                            <div className="text-xs text-[#666]">No purchase intent events recorded yet.</div>
                          ) : (
                            <div className="space-y-1 max-h-[220px] overflow-y-auto">
                              {(hub360.commerce?.recent_purchase_intents || []).map((row) => (
                                <div key={row.id} className="bg-[#0d0d0d] border border-[#1c1c1c] p-2 text-xs flex items-center justify-between gap-2">
                                  <span className="text-white">{row.tool_name || row.tool_id}</span>
                                  <span className="text-[#888]">{row.source || 'unknown source'}</span>
                                  <span className="text-[#888]">{row.timestamp ? new Date(row.timestamp).toLocaleString() : '-'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : activeConsultingTab === 'documents' ? (
                  <>
                    <div className="flex overflow-x-auto border-b border-[#1c1c1c] mb-4 consulting-tab-strip">
                      {DOC_TYPES.map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveDocTab(tab)}
                          className="font-mono text-[10px] tracking-widest uppercase px-4 py-3 border-b-2 whitespace-nowrap"
                          style={{ borderColor: activeDocTab === tab ? DOC_META[tab].accent : 'transparent', color: activeDocTab === tab ? DOC_META[tab].accent : '#666' }}
                        >
                          {DOC_META[tab].label}
                        </button>
                      ))}
                    </div>

                    <h2 className="font-unbounded text-sm font-semibold text-white">{DOC_META[activeDocTab].label}</h2>
                    <p className="text-sm text-[#888] mt-1 mb-3">{DOC_META[activeDocTab].desc}</p>

                    <div className="flex gap-2 flex-wrap mb-4">
                      {DOC_META[activeDocTab].chips.map((chip) => (
                        <span key={chip} className="bg-[rgba(255,232,0,0.04)] border border-[rgba(255,232,0,0.15)] text-[#FFE800] font-mono text-[9px] px-2 py-0.5 tracking-wider">{chip}</span>
                      ))}
                    </div>

                    <div className="mb-4">
                      <p className="field-heading mb-2">Consultant Notes</p>
                      <textarea
                        rows={3}
                        value={selectedClient.notes || ''}
                        onChange={(e) => {
                          const next = e.target.value;
                          setClients((prev) => prev.map((client) => (client.id === selectedClient.id ? { ...client, notes: next } : client)));
                        }}
                        onBlur={(e) => saveNotes(selectedClient.id, e.target.value)}
                        className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                      />
                    </div>

                    <div className="flex gap-2 flex-wrap mb-4">
                      {(selectedClient.pains || []).map((pain) => (
                        <span key={pain} className="font-mono text-[10px] px-2 py-1 border border-[#1c1c1c] text-[#888]">{pain}</span>
                      ))}
                    </div>

                    <div className="flex gap-2 mb-3">
                      <button onClick={() => handleGenerate(selectedClient.id, activeDocTab)} disabled={loading === activeDocTab} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-60">
                        {loading === activeDocTab ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Generating...</span> : 'Generate'}
                      </button>
                      <button onClick={copyOutput} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[#1c1c1c] text-[#888]">Copy</button>
                      <button onClick={() => handleGenerate(selectedClient.id, activeDocTab)} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[#1c1c1c] text-[#888]">Regenerate</button>
                    </div>

                    {docs[docKey(selectedClient.id, activeDocTab)] ? (
                      <pre className="bg-[#040404] border border-[rgba(255,232,0,0.12)] p-4 font-mono text-xs text-[#c8c8c8] leading-relaxed whitespace-pre-wrap max-h-[520px] overflow-y-auto">{docs[docKey(selectedClient.id, activeDocTab)]}</pre>
                    ) : (
                      <div className="border border-dashed border-[#1c1c1c] p-5 text-[#666] text-sm">Click Generate to create this document for {selectedClient.bizName}</div>
                    )}
                  </>
                ) : activeConsultingTab === 'orchestration' ? (
                  <>
                    <h2 className="font-unbounded text-sm font-semibold text-white">Orchestration Lab (Client Profile Linked)</h2>
                    <p className="text-sm text-[#888] mt-1 mb-4">Sync this client profile into orchestration, add custom workflows, and generate a long-form PDF business pitch package.</p>

                    <div className="flex gap-2 flex-wrap mb-4">
                      <button onClick={syncSelectedClientToOrchestration} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(57,255,20,0.35)] text-[#39FF14] bg-[rgba(57,255,20,0.08)] hover:bg-[rgba(57,255,20,0.14)] disabled:opacity-60">
                        {orchestrationBusy === 'sync' ? 'Syncing...' : 'Sync Client Profile'}
                      </button>
                      <button onClick={startDiscovery} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(0,212,255,0.35)] text-[#00D4FF] bg-[rgba(0,212,255,0.08)] hover:bg-[rgba(0,212,255,0.14)] disabled:opacity-60">
                        {orchestrationBusy === 'discovery' ? 'Starting...' : 'Start Discovery Lifecycle'}
                      </button>
                      <button onClick={generatePitchPackage} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-60">
                        {orchestrationBusy === 'pitch' ? 'Generating...' : 'Generate Pitch Package'}
                      </button>
                      <button onClick={downloadPitchPdf} disabled={orchestrationBusy !== '' || !pitchPackage} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[#1c1c1c] text-[#888] disabled:opacity-60">
                        {orchestrationBusy === 'download-pdf' ? 'Preparing...' : 'Download PDF'}
                      </button>
                    </div>

                    {syncResponse && (
                      <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3 mb-4 text-xs text-[#c8c8c8] font-mono">
                        Synced tenant: {syncResponse.tenant_id} | business type: {syncResponse.business_type}
                      </div>
                    )}

                    <div className="border border-[#1c1c1c] p-4 mb-4">
                      <p className="field-heading mb-2">Add Custom Workflow On Client Request</p>
                      <div className="grid md:grid-cols-2 gap-2 mb-2">
                        <input
                          value={customWorkflowForm.workflowName}
                          onChange={(e) => setCustomWorkflowForm((prev) => ({ ...prev, workflowName: e.target.value }))}
                          placeholder="Workflow name (e.g., Proposal QA Copilot)"
                          className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                        />
                        <input
                          value={customWorkflowForm.fallbackTools}
                          onChange={(e) => setCustomWorkflowForm((prev) => ({ ...prev, fallbackTools: e.target.value }))}
                          placeholder="Fallback tools comma-separated"
                          className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                        />
                      </div>
                      <textarea
                        rows={3}
                        value={customWorkflowForm.objective}
                        onChange={(e) => setCustomWorkflowForm((prev) => ({ ...prev, objective: e.target.value }))}
                        placeholder="Objective and expected business result"
                        className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none mb-2"
                      />
                      <button onClick={createCustomWorkflow} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-60">
                        {orchestrationBusy === 'custom-workflow' ? 'Saving...' : 'Save Custom Workflow'}
                      </button>
                    </div>

                    <div className="mb-4">
                      <p className="field-heading mb-2">Custom Workflows</p>
                      {customWorkflows.length === 0 ? (
                        <div className="border border-dashed border-[#1c1c1c] p-3 text-[#666] text-sm">No custom workflows added yet.</div>
                      ) : (
                        <div className="space-y-2">
                          {customWorkflows.map((workflow) => (
                            <div key={workflow.id || workflow.recipe_key} className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                              <p className="text-sm text-white font-semibold">{workflow.workflow_name}</p>
                              <p className="text-xs text-[#888] mt-1">{workflow.objective}</p>
                              <p className="text-[10px] text-[#39FF14] font-mono mt-2">{workflow.recipe_key}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="field-heading mb-2">Pitch Package Snapshot</p>
                      {pitchPackage ? (
                        <pre className="bg-[#040404] border border-[rgba(57,255,20,0.18)] p-4 font-mono text-xs text-[#c8c8c8] leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-y-auto">{JSON.stringify(pitchPackage, null, 2)}</pre>
                      ) : (
                        <div className="border border-dashed border-[#1c1c1c] p-5 text-[#666] text-sm">Generate pitch package to preview ROI charts data and long-form recommendations.</div>
                      )}
                    </div>
                  </>
                ) : activeConsultingTab === 'calendar_settings' ? (
                  <>
                    <h2 className="font-unbounded text-sm font-semibold text-white">Consultation Calendar Settings</h2>
                    <p className="text-sm text-[#888] mt-1 mb-4">Configure weekly windows shown in Consulting - Get Started. Client view auto-builds 20-minute slots with 20-minute gaps from these windows.</p>

                    <div className="border border-[#1c1c1c] p-4 space-y-3">
                      <div>
                        <p className="field-heading mb-2">Calendar Title</p>
                        <input
                          value={calendarSettingsForm.title}
                          onChange={(e) => setCalendarSettingsForm((prev) => ({ ...prev, title: e.target.value }))}
                          className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                          placeholder="Book a 20-minute consultation"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="field-heading mb-2">Timezone Label</p>
                          <input
                            value={calendarSettingsForm.timezone}
                            onChange={(e) => setCalendarSettingsForm((prev) => ({ ...prev, timezone: e.target.value }))}
                            className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                            placeholder="Local timezone confirmed by email"
                          />
                        </div>
                        <div>
                          <p className="field-heading mb-2">Booking URL (optional)</p>
                          <input
                            value={calendarSettingsForm.booking_url}
                            onChange={(e) => setCalendarSettingsForm((prev) => ({ ...prev, booking_url: e.target.value }))}
                            className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                            placeholder="https://cal.com/your-team/discovery"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="field-heading mb-0">Weekly Availability Editor</p>
                          <p className="text-xs text-[#666]">{calendarAvailability.size} slots selected</p>
                        </div>
                        <p className="text-xs text-[#666] mb-2">Click blocks directly to open/close slots. Each block is a 20-minute call start with an automatic 20-minute gap.</p>

                        <div className="flex gap-2 mb-3">
                          <button
                            type="button"
                            onClick={applyBusinessHoursPreset}
                            className="rounded-none font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-[#1c1c1c] text-[#aaa] hover:text-white"
                          >
                            Preset Mon-Fri 9-12, 1-5
                          </button>
                          <button
                            type="button"
                            onClick={clearAvailability}
                            className="rounded-none font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-[#1c1c1c] text-[#aaa] hover:text-white"
                          >
                            Clear All
                          </button>
                        </div>

                        <div className="border border-[#1c1c1c] bg-[#0a0a0a] overflow-auto max-h-[560px]">
                          <div className="min-w-[860px]">
                            <div className="grid" style={{ gridTemplateColumns: '110px repeat(7, minmax(100px, 1fr))' }}>
                              <div className="border-b border-r border-[#1c1c1c] p-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">Time</div>
                              {WEEKDAY_LABELS.map((day) => (
                                <div key={day.label} className="border-b border-r border-[#1c1c1c] p-2 text-[10px] font-mono uppercase tracking-widest text-[#888] text-center">{day.label}</div>
                              ))}

                              {GRID_MINUTES.map((minuteValue) => (
                                <React.Fragment key={minuteValue}>
                                  <div className="border-b border-r border-[#1c1c1c] p-2 text-xs text-[#888]">{formatMinutes(minuteValue)}</div>
                                  {WEEKDAY_LABELS.map((day) => {
                                    const isSelected = calendarAvailability.has(slotId(day.index, minuteValue));
                                    return (
                                      <button
                                        type="button"
                                        key={`${day.index}-${minuteValue}`}
                                        onClick={() => toggleAvailabilityCell(day.index, minuteValue)}
                                        className={`border-b border-r border-[#1c1c1c] h-9 transition-colors ${isSelected ? 'bg-[rgba(255,232,0,0.18)] text-[#FFE800]' : 'bg-transparent text-[#666] hover:bg-[#111]'}`}
                                        aria-label={`${day.label} ${formatMinutes(minuteValue)}`}
                                        title={`${day.label} ${formatMinutes(minuteValue)}`}
                                      >
                                        {isSelected ? 'Open' : ''}
                                      </button>
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button onClick={saveCalendarSettings} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)]">
                          Save Calendar Settings
                        </button>
                        <button onClick={() => loadCalendarSettings(adminKey)} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[#1c1c1c] text-[#888]">
                          Reload
                        </button>
                      </div>
                    </div>
                  </>
                ) : activeConsultingTab === 'user_management' ? (
                  <>
                    <h2 className="font-unbounded text-sm font-semibold text-white">Client User Management</h2>
                    <p className="text-sm text-[#888] mt-1 mb-4">Dedicated credential reset and profile diagnostics, separated from orchestration and PDF workflows.</p>

                    {!userManagement ? (
                      <div className="border border-dashed border-[#1c1c1c] p-5 text-[#666] text-sm">Loading client user profile...</div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Onboarding status</p>
                            <p className="text-sm text-[#FFE800] uppercase tracking-wider">{String(userManagement.onboarding_status || 'unknown')}</p>
                          </div>
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Stack profile</p>
                            <p className="text-sm text-white">{userManagement.stack_summary?.items_count || 0} tools</p>
                            <p className="text-xs text-[#888] mt-1">${userManagement.stack_summary?.total_monthly_cost || 0}/mo estimated</p>
                          </div>
                          <div className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                            <p className="field-heading mb-2">Recent interactions</p>
                            <p className="text-sm text-white">{userManagement.interactions?.sessions_count || 0} sessions</p>
                            <p className="text-xs text-[#888] mt-1">Consulting booked: {userManagement.interactions?.consulting_booked_count || 0}</p>
                          </div>
                        </div>

                        <div className="border border-[#1c1c1c] p-4">
                          <p className="field-heading mb-2">Account Recovery / Credential Reset</p>
                          <div className="grid md:grid-cols-2 gap-2 mb-2">
                            <input
                              value={credentialForm.newUsername}
                              onChange={(e) => setCredentialForm((prev) => ({ ...prev, newUsername: e.target.value }))}
                              placeholder="New username (optional)"
                              className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                            />
                            <input
                              type="password"
                              value={credentialForm.newPassword}
                              onChange={(e) => setCredentialForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                              placeholder="New password (optional)"
                              className="bg-[#0a0a0a] border border-[#1c1c1c] text-white text-sm px-3 py-2 rounded-none w-full focus:border-[#FFE800] outline-none"
                            />
                          </div>
                          <button onClick={submitCredentialReset} disabled={orchestrationBusy !== ''} className="rounded-none font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-[rgba(255,232,0,0.35)] text-[#FFE800] bg-[rgba(255,232,0,0.08)] hover:bg-[rgba(255,232,0,0.14)] disabled:opacity-60">
                            {orchestrationBusy === 'reset' ? 'Updating...' : 'Apply Credential Reset'}
                          </button>
                        </div>

                        <div className="border border-[#1c1c1c] p-4">
                          <p className="field-heading mb-2">Profile Snapshot</p>
                          <pre className="bg-[#040404] border border-[#1c1c1c] p-3 font-mono text-xs text-[#c8c8c8] leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-y-auto">{JSON.stringify(userManagement, null, 2)}</pre>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h2 className="font-unbounded text-sm font-semibold text-white">AI Readiness &amp; Session Stats</h2>
                    <p className="text-sm text-[#888] mt-1 mb-4">Client-reported readiness profile, ROI snapshot, and My AI Flow simulation sessions.</p>

                    {!readinessStats ? (
                      <div className="border border-dashed border-[#1c1c1c] p-5 text-[#666] text-sm">No readiness data available for this client yet.</div>
                    ) : (
                      <div className="space-y-4">
                        {/* Completion Badge */}
                        <div className="flex items-center gap-3">
                          <span className={`font-mono text-[10px] px-3 py-1 uppercase tracking-widest border ${readinessStats.readiness_completed ? 'border-[rgba(57,255,20,0.4)] text-[#39FF14] bg-[rgba(57,255,20,0.08)]' : 'border-[#1c1c1c] text-[#888]'}`}>
                            {readinessStats.readiness_completed ? 'Readiness Complete' : 'Not Yet Completed'}
                          </span>
                          {readinessStats.last_ai_flow_session_at && (
                            <span className="font-mono text-[10px] text-[#888]">Last session: {new Date(readinessStats.last_ai_flow_session_at).toLocaleDateString()}</span>
                          )}
                        </div>

                        {/* ROI Snapshot Grid */}
                        {readinessStats.readiness_snapshot && Object.keys(readinessStats.readiness_snapshot).length > 0 && (
                          <div>
                            <p className="field-heading mb-2">ROI Snapshot</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {[
                                { label: 'Readiness Score', key: 'readiness_score', unit: '/100' },
                                { label: 'Automation Potential', key: 'automation_potential', unit: '%' },
                                { label: 'Annual AI Savings', key: 'annual_ai_savings', fmt: 'currency' },
                                { label: 'Payback', key: 'payback_months', unit: ' mo' },
                              ].map(({ label, key, unit, fmt }) => {
                                const val = readinessStats.readiness_snapshot[key];
                                const display = val == null ? '-' : fmt === 'currency'
                                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
                                  : `${val}${unit || ''}`;
                                return (
                                  <div key={key} className="bg-[#0d0d0d] border border-[#1c1c1c] p-3">
                                    <p className="field-heading mb-1" style={{ fontSize: '9px' }}>{label}</p>
                                    <p className="text-sm font-semibold text-[#FFE800]">{display}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Business Profile */}
                        {readinessStats.readiness_profile_full?.business_profile && (
                          <div>
                            <p className="field-heading mb-2">Business Profile</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {Object.entries(readinessStats.readiness_profile_full.business_profile).map(([k, v]) => (
                                <div key={k} className="bg-[#0d0d0d] border border-[#1c1c1c] p-2">
                                  <p className="font-mono text-[9px] text-[#888] uppercase tracking-wider mb-1">{k.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-white">{Array.isArray(v) ? v.join(', ') || '-' : String(v ?? '-')}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Session Stats Table */}
                        <div>
                          <p className="field-heading mb-2">Session History ({(readinessStats.session_stats || []).length} recorded)</p>
                          {(readinessStats.session_stats || []).length === 0 ? (
                            <div className="border border-dashed border-[#1c1c1c] p-3 text-[#666] text-sm">No simulation sessions recorded yet.</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full font-mono text-xs">
                                <thead>
                                  <tr className="border-b border-[#1c1c1c] text-[#888] uppercase tracking-wider text-[9px]">
                                    <th className="text-left py-2 pr-3">Date</th>
                                    <th className="text-left py-2 pr-3">Business Type</th>
                                    <th className="text-right py-2 pr-3">Completed</th>
                                    <th className="text-right py-2 pr-3">AI Savings</th>
                                    <th className="text-center py-2">PDF</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...(readinessStats.session_stats || [])].reverse().map((session, idx) => (
                                    <tr key={idx} className="border-b border-[#111] hover:bg-[#0d0d0d]">
                                      <td className="py-2 pr-3 text-[#888]">{session.recorded_at ? new Date(session.recorded_at).toLocaleDateString() : '-'}</td>
                                      <td className="py-2 pr-3 text-white">{session.business_type || '-'}</td>
                                      <td className="py-2 pr-3 text-right text-[#FFE800]">{session.completed_runs ?? '-'}/{session.total_runs ?? '-'}</td>
                                      <td className="py-2 pr-3 text-right text-[#39FF14]">
                                        {session.annual_ai_savings
                                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(session.annual_ai_savings)
                                          : '-'}
                                      </td>
                                      <td className="py-2 text-center">{session.pdf_generated ? <span className="text-[#00D4FF]">✓</span> : <span className="text-[#444]">-</span>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminConsultingPage;

